import { useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { IconSearch } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import DateRangePicker from '@/components/dateRangePicker';
import ExportDropdown from '@/components/ExportDropdown';
import Input from '@/components/Input/Input';
import SkeletonLoader from '@/components/SkeletonLoader';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import {
  getCommonStatusLabel,
  getCommonStatusBadgeClass,
} from '@/constants/constant';
import { EExportType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import {
  exportAnalyticsData,
  getTeacherAnalytics,
} from '@/service/classService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl, downloadFile, formatLastActive } from '@/utils';

interface ITeacherReport {
  id: string;
  name: string;
  email: string;
  status: string;
  profile_pic: string | null;
  total_classes: number;
  total_lessons: number;
  active_students: number;
  last_update?: number;
}

interface ITeachersTabForm {
  search: string;
}

const TeachersTab = () => {
  const { formatText } = useFormatText();
  const { isScreenLoading, isTableLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();
  const { settings } = useSettingsStore();

  const { register, watch } = useForm<ITeachersTabForm>({
    defaultValues: {
      search: '',
    },
  });

  const searchQuery = watch('search');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC';
  } | null>(null);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'ASC' ? 'DESC' : 'ASC' };
      }
      return { key, direction: 'ASC' };
    });
  };

  const [teacherData, setTeacherData] = useState<ITeacherReport[]>([]);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const pageSize = 10;

  const fetchTeachers = async () => {
    if (isFirstLoad || (teacherData.length === 0 && !debouncedSearchQuery)) {
      setScreenLoading(true);
    } else {
      setTableLoading(true);
    }
    try {
      const query: Record<string, unknown> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };
      if (debouncedSearchQuery) query.search = debouncedSearchQuery;
      if (startDate)
        query.start_date = Math.floor(new Date(startDate).getTime() / 1000);
      if (endDate)
        query.end_date = Math.floor(new Date(endDate).getTime() / 1000);

      if (sortConfig?.key && sortConfig?.direction) {
        query[`order[${sortConfig.key}]`] = sortConfig.direction;
      } else {
        query[`order[created_at]`] = 'DESC';
      }

      const response = await getTeacherAnalytics(query);
      if (response?.status && Array.isArray(response.data)) {
        setTeacherData(response.data);
        setTotalTeachers(response?.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch teacher analytics:', error);
    } finally {
      setScreenLoading(false);
      setTableLoading(false);
      setIsFirstLoad(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    debouncedSearchQuery,
    startDate,
    endDate,
    pageSize,
    isFirstLoad,
    sortConfig,
  ]);

  // ─── Table Columns ───────────────────────────────────────────────────────────

  const teacherColumns = useMemo(
    () => [
      {
        key: 'name',
        title: 'reports-analytics.table.teacher-name',
        width: '280px',
        sorter: true,
        handleSort: () => handleSort('name'),
        render: (record: ITeacherReport) => (
          <div className="flex items-center gap-3">
            {getAssetUrl(record?.profile_pic) ? (
              <img
                src={getAssetUrl(record.profile_pic)}
                alt={record.name}
                className="w-7 h-7 rounded-full object-cover shadow-md"
              />
            ) : (
              <UserAvatarList user={record.name} />
            )}
            <span className="text-sm font-medium text-neutral-primary">
              {record.name}
            </span>
          </div>
        ),
      },
      {
        key: 'email',
        title: 'reports-analytics.table.email',
        width: '280px',
        sorter: true,
        handleSort: () => handleSort('email'),
        render: (record: ITeacherReport) => (
          <span className="text-sm text-neutral-secondary">{record.email}</span>
        ),
      },

      {
        key: 'total_classes',
        title: 'reports-analytics.table.classes',
        align: 'center' as const,
        width: '150px',
        sorter: true,
        handleSort: () => handleSort('total_classes'),
        render: (record: ITeacherReport) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-sm font-medium">
            {record.total_classes ?? '-'}
          </span>
        ),
      },
      {
        key: 'total_lessons',
        title: 'reports-analytics.table.lessons-created',
        align: 'center' as const,
        width: '180px',
        sorter: true,
        handleSort: () => handleSort('total_lessons'),
        render: (record: ITeacherReport) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-pink-light-50 text-pink-secondary text-sm font-medium">
            {record.total_lessons ?? '-'}
          </span>
        ),
      },
      {
        key: 'active_students',
        title: 'reports-analytics.table.active-students',
        align: 'center' as const,
        width: '180px',
        sorter: true,
        handleSort: () => handleSort('active_students'),
        render: (record: ITeacherReport) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-success-light-50 text-success-default text-sm font-medium">
            {record.active_students ?? '-'}
          </span>
        ),
      },
      {
        key: 'status',
        title: 'reports-analytics.table.status',
        width: '120px',
        sorter: true,
        handleSort: () => handleSort('status'),
        render: (record: ITeacherReport) => (
          <TagCapsule
            value={formatText(getCommonStatusLabel(record.status))}
            className={getCommonStatusBadgeClass(record.status)}
          />
        ),
      },
      {
        key: 'last_update',
        title: 'reports-analytics.table.last-update',
        align: 'center' as const,
        width: '150px',
        sorter: true,
        handleSort: () => handleSort('last_update'),
        render: (record: ITeacherReport) => (
          <span className="text-sm text-neutral-secondary whitespace-nowrap">
            {record.last_update
              ? formatLastActive(record.last_update, formatText)
              : '-'}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings]
  );

  const handleExport = async (doc_type: 'csv' | 'pdf') => {
    setIsExportLoading(true);
    try {
      const query: Record<string, unknown> = {
        type: 3, // Teacher
      };
      if (debouncedSearchQuery) query.search = debouncedSearchQuery;
      if (startDate)
        query.start_date = Math.floor(new Date(startDate).getTime() / 1000);
      if (endDate)
        query.end_date = Math.floor(new Date(endDate).getTime() / 1000);
      if (selectedRowKeys.length > 0) query.ids = selectedRowKeys;
      if (doc_type === 'csv') {
        query.doc_type = EExportType.CSV;
      } else if (doc_type === 'pdf') {
        query.doc_type = EExportType.PDF;
      }

      const response = await exportAnalyticsData(query);
      if (response) {
        const label = formatText('reports-analytics.tabs.teachers')
          .replace(/\s+/g, '_')
          .toLowerCase();
        downloadFile(response, `${label}_${new Date().getTime()}.${doc_type}`);
      }
    } catch (error) {
      console.error('Failed to export teacher analytics:', error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const isFilterApplied = !!debouncedSearchQuery || !!startDate || !!endDate;

  const hasNoTeachers =
    !isFirstLoad &&
    !isScreenLoading &&
    teacherData.length === 0 &&
    !isFilterApplied;

  const isTeacherEmptySearch =
    !isFirstLoad &&
    !isScreenLoading &&
    teacherData.length === 0 &&
    isFilterApplied;

  return (
    <div className="flex flex-col gap-6" data-testid="teachers-tab-container">
      {/* Filters Section */}
      {!hasNoTeachers && (
        <div
          className="flex items-center justify-between gap-6 flex-wrap ps-1"
          data-testid="teachers-tab-filters"
        >
          <div className="flex flex-1 items-center gap-2.5 min-w-87.5 flex-wrap">
            <div className="w-87.5">
              {isScreenLoading ? (
                <SkeletonLoader className="h-11 w-full rounded-lg" />
              ) : (
                <Input
                  name="search"
                  placeholder={formatText(
                    'reports-analytics.teacher-searchbar'
                  )}
                  leftIcon={{
                    icon: <IconSearch size={18} className="text-neutral-400" />,
                  }}
                  register={register}
                  dataTestId="teacher-report-search-input"
                  className="max-w-70"
                  inputClassName="py-[13px]"
                />
              )}
            </div>
          </div>

          <div
            className="flex items-center gap-2.5"
            data-testid="teachers-tab-actions"
          >
            {isScreenLoading ? (
              <SkeletonLoader className="h-11 w-48 rounded-lg" />
            ) : (
              <DateRangePicker
                popupAlign="right"
                popUpClassName="right-0 rtl:left-0 rtl:right-auto"
                setFirstDate={setStartDate}
                setSecondDate={setEndDate}
                dataTestId="date-range-picker"
              />
            )}

            {isScreenLoading ? (
              <SkeletonLoader className="h-11 w-32 rounded-lg" />
            ) : (
              <ExportDropdown
                onCsvExport={() => handleExport('csv')}
                onPdfExport={() => handleExport('pdf')}
                disabled={teacherData?.length === 0 || isExportLoading}
              />
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl" data-testid="teachers-report-table">
        <CustomTable
          dataSource={teacherData}
          columns={teacherColumns}
          rowKey="id"
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={totalTeachers}
          pageSize={pageSize}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={setSelectedRowKeys}
          isEmptySearch={isTeacherEmptySearch}
          isEmpty={hasNoTeachers}
          showHeader={!hasNoTeachers}
          hidePagination={hasNoTeachers}
          emptyImgSrc="/not-found.svg"
          emptyTitle={formatText('reports-analytics.empty-title')}
          emptyDescription={formatText('reports-analytics.empty-description')}
          isLoading={isTableLoading || isFirstLoad}
          fieldName={sortConfig?.key}
          sortDirection={sortConfig?.direction}
          dataTestId="teachers-report-table-component"
        />
      </div>
    </div>
  );
};

export default TeachersTab;
